import { shiftDayKey } from "./days";
import { mondayKey, realDay, type WeekCell } from "./weekLine";
import { EMPTY_MARK } from "./emptyMark";
import { displayName } from "./roster";
import type { RosterData } from "./rosterData";

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THIS WEEK — the coach's landing (W72 step 1, Proposal 1): one board across every client.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Every row is one client; every sentence says WHAT needs the coach, in the coach's words
 * (Proposal 7 rule 7): `missed Wednesday` · `nothing after Sunday` · `3 changes proposed` ·
 * `invited 12 Sep · not joined`. Never "needs attention", never a count without its reason.
 *
 * 🔴 THE WEEK LINE IS NOT DERIVED HERE. It arrives as `WeekCell[]` from `rosterData.ts`, which is the
 * roster's own derivation (`weekCells`, the client page's rule too) — ONE reader, so the board can
 * never call a day missed that the roster calls done. This module only READS the cells.
 *
 * ⚠️ `changesProposed` IS NULL UNTIL ITS READ LANDS (Fable's ruling d): the proposal needs two tree
 * reads per client with a next week, fetched lazily after the board draws. Null means "not known
 * yet" and produces NO sentence — never a placeholder count — and the day line recounts when it
 * arrives.
 */

export type BoardInput = {
  linkId: string;
  clientId: string | null;
  /** `coach_clients.status` — only `active` rows are asked what they need. */
  status: string;
  /** The full display name (`roster.displayName`). */
  name: string;
  /** What the day line calls them — the first word of the name they carry. */
  firstName: string;
  /** `coach_clients.created_at` as `YYYY-MM-DD` — the day this coach wrote the invitation. */
  invitedISO: string | null;
  /** N2g — `coach_clients.ended_at` as `YYYY-MM-DD` (an ended row's `Ended 12 Sep`); absent/null = nobody ended it. */
  endedISO?: string | null;
  lastTrainedISO: string | null;
  /** The first training day assigned after this Sunday, or null — `rosterData.firstAfterWeekISO`. */
  firstAfterWeekISO: string | null;
  /** W111 — the LAST training day assigned (tasks never count), or null — `rosterData.lastAssignedISO`. ⚠️ Optional ONLY so
   *  hand-built inputs elsewhere keep compiling; ABSENT means "not read" and never makes a row critical. */
  lastAssignedISO?: string | null;
  /** This week's seven cells, or undefined for a row with no account behind it. */
  cells: WeekCell[] | undefined;
  /** How many rows next week moves against this one, once read; null while unknown or not applicable. */
  changesProposed: number | null;
};

export type Need =
  | { kind: "missed"; days: string[] }
  | { kind: "nothingAfterSunday" }
  | { kind: "changes"; n: number };

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-09-12` → `12 Sep`. An unreadable key is returned as it came, never guessed at. */
export function dayMonth(dayKey: string): string {
  const d = realDay(dayKey);
  if (d === null) return dayKey;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** `Friday 25 Sep` — the board's date, the way a coach says it. */
export function boardDate(today: Date): string {
  return `${WEEKDAY[today.getUTCDay()]} ${today.getUTCDate()} ${MONTHS[today.getUTCMonth()]}`;
}

/** What needs the coach about ONE active client, in order: the missed days, next week, the proposals. */
export function needsOf(c: BoardInput): Need[] {
  if (c.status !== "active") return [];
  const out: Need[] = [];
  const missed = (c.cells ?? []).filter((cell) => cell.state === "missed");
  if (missed.length > 0) {
    out.push({
      kind: "missed",
      days: missed.map((cell) => WEEKDAY[new Date(`${cell.dayKey}T00:00:00Z`).getUTCDay()] ?? cell.label),
    });
  }
  if (c.firstAfterWeekISO === null) out.push({ kind: "nothingAfterSunday" });
  if (c.changesProposed !== null && c.changesProposed > 0) out.push({ kind: "changes", n: c.changesProposed });
  return out;
}

function missedWords(days: string[]): string {
  if (days.length >= 3) return `missed ${days.length} days`;
  return `missed ${days.join(" and ")}`;
}

/** A need as the ROW says it — no name, lower case: `missed Wednesday`. */
export function needPhrase(n: Need): string {
  switch (n.kind) {
    case "missed": return missedWords(n.days);
    case "nothingAfterSunday": return "nothing after Sunday";
    case "changes": return `${n.n} ${n.n === 1 ? "change" : "changes"} proposed`;
  }
}

/** A need as the DAY LINE says it, after the name: `missed Wednesday`, `has nothing after Sunday`. */
function namedPhrase(n: Need): string {
  switch (n.kind) {
    case "missed": return missedWords(n.days);
    case "nothingAfterSunday": return "has nothing after Sunday";
    case "changes": return `has ${n.n} ${n.n === 1 ? "change" : "changes"} proposed`;
  }
}

function andList(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The row's sentence. An invited client is told what they are waiting on; an active one what needs
 * the coach, several joined by ` · `; nothing at all is the empty string, and the row says nothing.
 */
/* QUEUE N2g (2026-10-06) — ARCHIVED = `declined` ∪ `ended`, the phone's word (`CoachRosterFacts`, Fable's W83-3 ruling: no
 * archived state exists, none is added; a withdrawn link is `ended` too). The web read them as "Invited · not joined yet" with a
 * Copy link the claim refuses (it takes `pending` only — MEASURED live), and counted them as invited. An archived row is still
 * NOT ACTIVE (`invited` stays "not active", so every active count is untouched); only the pending-invitation sites ask this. */
export function isArchived(status: string): boolean {
  return status === "declined" || status === "ended";
}

export function needsYou(c: BoardInput): string {
  if (c.status === "declined") return "declined";
  if (c.status === "ended") return c.endedISO == null ? "ended" : `ended ${dayMonth(c.endedISO)}`;
  if (c.status !== "active") {
    return c.invitedISO === null ? "invited · not joined" : `invited ${dayMonth(c.invitedISO)} · not joined`;
  }
  return needsOf(c).map(needPhrase).join(" · ");
}

export type BoardRow = BoardInput & { invited: boolean; archived: boolean; needs: Need[]; sentence: string };

/** The roster's caption under a not-active name: `Invited 3 Oct · not joined yet` · `Declined` · `Ended 12 Sep`. */
export function notActiveCaption(r: Pick<BoardRow, "archived" | "sentence">): string {
  const s = r.sentence.charAt(0).toUpperCase() + r.sentence.slice(1);
  return r.archived ? s : `${s} yet`;
}

/**
 * The board's order: active clients who need the coach, then the other active ones, then the
 * invited — each by last trained, most recent first (nothing trained last), then by name.
 */
export function boardRows(inputs: readonly BoardInput[]): BoardRow[] {
  const rows = inputs.map((c) => ({
    ...c,
    invited: c.status !== "active",
    archived: isArchived(c.status),
    needs: needsOf(c),
    sentence: needsYou(c),
  }));
  const group = (r: BoardRow) => (r.archived ? 3 : r.invited ? 2 : r.needs.length > 0 ? 0 : 1);
  return rows.sort((a, b) => {
    const g = group(a) - group(b);
    if (g !== 0) return g;
    const at = a.lastTrainedISO ?? "";
    const bt = b.lastTrainedISO ?? "";
    if (at !== bt) return at < bt ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
}

/** Who needs the coach, named: `William missed Wednesday` · `Yiannis has nothing after Sunday`. */
export function namedNeeds(rows: readonly BoardRow[]): string[] {
  return rows
    .filter((r) => !r.invited && r.needs.length > 0)
    .map((r) => `${r.firstName} ${andList(r.needs.map(namedPhrase))}`);
}

/* The Clients day line (`Friday 25 Sep · 2 need you: …`, `dayLine`) went 2026-10-15 with William's first look — it said the
   Dashboard's To do again in prose; `namedNeeds` stays as the tile sentence's reader. */

/** How many ACTIVE clients trained this week (a `done` cell) — the Dashboard's tile, ONE reading. */
export function trainedThisWeek(rows: readonly BoardRow[]): { trained: number; active: number } {
  const active = rows.filter((r) => !r.invited);
  return { trained: active.filter((r) => (r.cells ?? []).some((c) => c.state === "done")).length, active: active.length };
}

/** The week the board is about, Monday to Sunday: `21 – 27 Sep` or `28 Sep – 4 Oct`. */
export function boardWeek(today: Date): string {
  const monday = mondayKey(today, 0);
  const sunday = shiftDayKey(new Date(`${monday}T00:00:00Z`), 6);
  const a = dayMonth(monday);
  const b = dayMonth(sunday);
  return monday.slice(0, 7) === sunday.slice(0, 7) ? `${a.split(" ")[0]} – ${b}` : `${a} – ${b}`;
}

/** Whether the lazy proposal read applies to a row at all: an active client whose next week exists. */
export function wantsProposal(c: Pick<BoardInput, "status" | "firstAfterWeekISO">): boolean {
  return c.status === "active" && c.firstAfterWeekISO !== null;
}

const SHORT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * The TRAINED column: `Today`, a weekday within the last six days (`Mon`), older as `12 Sep`, and a
 * dash when nothing is inside the roster's look-back — never "never", which the look-back cannot know.
 */
export function trainedWord(lastTrainedISO: string | null, today: Date): string {
  if (lastTrainedISO === null) return EMPTY_MARK;
  const d = realDay(lastTrainedISO);
  if (d === null) return lastTrainedISO;
  const todayKey = today.toISOString().slice(0, 10);
  if (lastTrainedISO === todayKey) return "Today";
  const days = Math.round((new Date(`${todayKey}T00:00:00Z`).getTime() - d.getTime()) / 86_400_000);
  if (days > 0 && days <= 6) return SHORT_DAY[d.getUTCDay()] ?? lastTrainedISO;
  return dayMonth(lastTrainedISO);
}

/**
 * The NEXT ASSIGNED column: the first training day after this week (`Mon 28 Sep`), or the one thing
 * on the row that needs action, said: `Nothing after Sun`. It reads `firstAfterWeekISO` — the SAME
 * fact as the board's "nothing after Sunday" — so the two screens cannot disagree.
 */
export function nextAssignedWord(firstAfterWeekISO: string | null): { text: string; needsAction: boolean } {
  if (firstAfterWeekISO === null) return { text: "Nothing after Sun", needsAction: true };
  const d = realDay(firstAfterWeekISO);
  if (d === null) return { text: firstAfterWeekISO, needsAction: false };
  return { text: `${SHORT_DAY[d.getUTCDay()]} ${dayMonth(firstAfterWeekISO)}`, needsAction: false };
}

/* W126 (his word, 2026-10-01) — `captionOf` (`Calisthenics skills · 6 days`, the line under a Clients row's name) is GONE
 * with the line: its one reader was that row. The goal and the days a week are the client's Profile tab (`profileTab.ts`).
 * Its finding stays true: the web has no block position for a coached client, so no "Week 5 of 8" is ever invented. */

/**
 * The roster's data as the board's input, one row per coaching link. W81 (the pro web step 1) moved it here out of
 * `ThisWeek.tsx` so the sidebar's needs-you badge and the page's own count are ONE reading — a second copy of this map in
 * the shell would be the count and the page disagreeing the first time either moved.
 */
export function boardInputsOf(data: RosterData, proposals: Readonly<Record<string, number>>): BoardInput[] {
  return data.rows.map((r) => {
    const invited = data.invited[r.linkId] ?? { firstName: null, lastName: null, email: null };
    const name = displayName(r, invited);
    return {
      linkId: r.linkId,
      clientId: r.clientId,
      status: r.status,
      name,
      firstName: (r.firstName ?? invited.firstName ?? name).trim().split(/\s+/)[0] ?? name,
      invitedISO: r.invitedISO,
      lastTrainedISO: r.lastTrainedISO,
      firstAfterWeekISO: data.firstAfterWeekISO[r.linkId] ?? null,
      lastAssignedISO: data.lastAssignedISO[r.linkId] ?? null,
      cells: data.weeks[r.linkId],
      changesProposed: proposals[r.linkId] ?? null,
    };
  });
}
